import { Component, type ErrorInfo, type ReactNode } from 'react';
import { captureException } from '@sentry/browser';

import { requestRecovery } from '@/lib/recovery';
import { SignyLogo } from './SignyLogo';

/**
 * Catches render-time crashes anywhere below it.
 *
 * Without a boundary, an uncaught render error unmounts the entire React tree
 * and leaves an empty #root — which paints as a plain white screen that never
 * recovers, with nothing on screen to identify it.
 *
 * NOTE: a boundary also STOPS React rethrowing to window.onerror, which is how
 * these errors currently reach Sentry. So the boundary must report explicitly —
 * otherwise adding it would reduce visibility rather than improve it.
 */

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // The exception carries the stack, so it is the more useful of the two
    // possible reports — requestRecovery is told not to send its own.
    captureException(error, {
      tags: { failureKind: 'render-crash' },
      extra: { componentStack: errorInfo.componentStack },
    });

    // No-op unless auto-recovery has been enabled; the report above stands
    // either way.
    requestRecovery('render-crash', { message: error.message }, { report: false });
  }

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    // Deliberately not a blank screen: an identifiable state tells whoever
    // walks past that the screen failed, rather than looking like a dead panel.
    return (
      <div className="flex h-screen w-full flex-col items-center justify-center space-y-8 bg-white">
        <div className="w-[300px]">
          <SignyLogo />
        </div>
        <p className="w-1/2 text-center text-lg font-semibold">
          Ett fel uppstod på skärmen. Felet har rapporterats.
        </p>
      </div>
    );
  }
}
